// React Context Provider for WebSocket sync
// Makes WebSocket client available throughout the component tree

import React, { createContext, useContext, ReactNode } from 'react';
import { useWebSocketSync, UseWebSocketSyncResult, UseWebSocketSyncOptions } from './useWebSocketSync';

const WebSocketContext = createContext<UseWebSocketSyncResult | null>(null);

export interface WebSocketProviderProps extends UseWebSocketSyncOptions {
  children: ReactNode;
}

export function WebSocketProvider({ children, ...options }: WebSocketProviderProps) {
  const websocket = useWebSocketSync(options);

  return (
    <WebSocketContext.Provider value={websocket}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket(): UseWebSocketSyncResult {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
}

// Hook for components that only need connection status
export function useWebSocketStatus() {
  const { status, isConnected } = useWebSocket();
  return { status, isConnected };
}

// Hook for components that need the client instance for pull/push operations
export function useWebSocketClient() {
  const { client, isConnected } = useWebSocket();
  return { client, isConnected };
}
